import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Chip, inputCls } from "../components/Chips";
import { SongRow } from "../components/SongRow";
import { http } from "../lib/api";
import { trashSong } from "../lib/trash";
import { TrashList } from "../components/TrashList";

const ENDPOINT = { mine: "/songs/mine", liked: "/songs/liked", trash: "/songs/trash" };

const useLibrary = (tab) => {
  const [songs, setSongs] = useState(null);
  const load = useCallback(() => http.get(ENDPOINT[tab]).then((r) => setSongs(r.data)), [tab]);
  useEffect(() => { setSongs(null); load(); }, [load]);
  const actions = {
    like: async (s) => { await http.post(`/songs/${s.id}/like`); load(); },
    del: (s) => trashSong(s, load),
    rename: async (s) => {
      const t = window.prompt("Naujas dainos pavadinimas", s.title)?.trim();
      if (!t || t === s.title) return;
      await http.patch(`/songs/${s.id}`, { title: t.slice(0, 80) });
      toast.success("Pavadinimas pakeistas");
      load();
    },
    vis: async (s) => {
      await http.patch(`/songs/${s.id}`, { is_public: !s.is_public });
      toast.success(s.is_public ? "Daina dabar privati" : "Daina rodoma „Atrasti“");
      load();
    },
  };
  return { songs, load, actions };
};

const TABS = [["mine", "Mano dainos"], ["liked", "Patinka"], ["trash", "Šiukšliadėžė"]];

const Toolbar = ({ tab, setTab, q, setQ }) => (
  <div className="flex flex-col sm:flex-row gap-4 justify-between">
    <div className="flex gap-2">
      {TABS.map(([id, label]) => <Chip key={id} testId={`library-tab-${id}`} active={tab === id} onClick={() => setTab(id)}>{label}</Chip>)}
    </div>
    <div className="relative sm:w-72">
      <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
      <input data-testid="library-search-input" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-10 py-2.5`} placeholder="Filtruoti..." />
    </div>
  </div>
);

const EMPTY = { mine: ["Dar neturite dainų.", "/", "Kurti dainą"], liked: ["Dar nieko nepamėgote.", "/atrasti", "Atrasti dainų"] };

const EmptyState = ({ tab }) => {
  const [text, to, cta] = EMPTY[tab];
  return (
    <div data-testid="library-empty" className="text-center py-20 rounded-3xl border border-dashed border-[var(--c-line)]">
      <p className="text-zinc-400">{text} <Link to={to} className="text-[var(--c-orange)] hover:underline">{cta}</Link></p>
    </div>
  );
};

const SongList = ({ tab, shown, actions }) => {
  if (!shown.length) return <EmptyState tab={tab} />;
  const playable = shown.filter((s) => s.status === "completed");
  return (
    <div data-testid="library-list" className="space-y-1">
      {shown.map((s) => <SongRow key={s.id} song={s} list={playable} onLike={actions.like} onDelete={actions.del} onTogglePublic={actions.vis} onRename={actions.rename} />)}
    </div>
  );
};

export default function Library() {
  const [tab, setTab] = useState("mine");
  const [q, setQ] = useState("");
  const { songs, load, actions } = useLibrary(tab);
  const shown = (songs || []).filter((s) => `${s.title} ${s.style}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-5xl space-y-8">
      <header className="rise">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold mb-4">Kolekcija</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold">Biblioteka</h1>
      </header>
      <Toolbar tab={tab} setTab={setTab} q={q} setQ={setQ} />
      {songs && (tab === "trash" ? <TrashList songs={shown} reload={load} /> : <SongList tab={tab} shown={shown} actions={actions} />)}
    </div>
  );
}
